import React from 'react';
import * as Yup from 'yup';
import allForm from '../../stateless/Form';
import { Box, Button, Typography, alpha, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import PhoneNumberInput from '../Utils/PhoneNumberInput';
import { useEffect, useMemo, useState } from 'react';
import { COLORS } from '../../../theme/themeConstants';
import { useEditProfile } from '../../Query/ProfileQuery';
import { useTranslation } from 'react-i18next';
import { splitPhoneNumber } from '../../../utils/heplingFuctions';

// Note: file/image validation moved to Settings page

const nameValidation = Yup.string()
  .required('Please enter name')
  .max(50, 'Name cannot exceed 50 characters')
  .test(
    'no-leading-trailing-spaces',
    'Name cannot start or end with spaces',
    (value) => value?.trim() === value
  )
  .test('not-only-spaces', 'Name cannot be only spaces', (value) =>
    (value || '').trim().length > 0
  )
  .test('not-only-numbers', 'Name cannot be only numbers', (value) => {
    if (!value) return false;
    return !/^\d+$/.test(value.trim());
  })
  .test('not-start-with-number', 'Name cannot start with a number', (value) => {
    if (!value) return false;
    return !/^\d/.test(value.trim());
  });

const optionalNameValidation = Yup.string()
  .max(50, 'Name cannot exceed 50 characters')
  .test(
    'no-leading-trailing-spaces',
    'Name cannot start or end with spaces',
    (value) => (value ? value.trim() === value : true)
  )
  .test('not-only-spaces', 'Name cannot be only spaces', (value) =>
    value ? value.trim().length > 0 : true
  )
  .test('not-only-numbers', 'Name cannot be only numbers', (value) => {
    if (!value) return true;
    return !/^\d+$/.test(value.trim());
  })
  .test('not-start-with-number', 'Name cannot start with a number', (value) => {
    if (!value) return true;
    return !/^\d/.test(value.trim());
  });

// const imageRequired = (label: string) =>
//   Yup.mixed().test('image-required', `${label} is required`, (v) => {
//     if (Array.isArray(v)) return v.length > 0;
//     if (typeof v === 'string') return v.trim().length > 0;
//     return !!v;
//   });

export type OnClose = { onClose?: () => void };

export const EditProfileForm: React.FC<OnClose> = ({ onClose }) => {
  const { mutate: EditProfile, isPending: EditProfileLoading } = useEditProfile();
  const { t } = useTranslation();

  // const allowedCountryCodes = ['+971', '+966', '+965', '+973', '+974', '+968', '+20', '+962', '+961', '+964', '+92'];
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('+962');
  const [initialValues, setInitialValues] = useState({ name: '', lastName: '', email: '', mobile: '', preferredLanguage: 'en' });

  const loadUserFromLocalStorage = () => {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) return;
      const data = JSON.parse(raw);
      const user = data?.userDetails || data || {};
      const fullMobile: string = String(user?.mobileNo || user?.mobile || '');
      const digitsOnly = fullMobile.replace(/\s|-/g, '');
      // const isWithCode = digitsOnly.length > 10;
      

      // let extractedCode = isWithCode ? digitsOnly.slice(0, digitsOnly.length - 10) : selectedCountryCode;
      // if (typeof extractedCode === 'string') {
      //   extractedCode = extractedCode.trim();
      //   if (extractedCode.startsWith('00')) extractedCode = `+${extractedCode.slice(2)}`;
      //   else if (!extractedCode.startsWith('+')) extractedCode = `+${extractedCode}`;
      // }
      // const normalizedCode = allowedCountryCodes.includes(extractedCode) ? extractedCode : '+962';

  const {countryCode:normalizedCode, phone:localPart} = splitPhoneNumber(digitsOnly)

      // const localPart = isWithCode ? digitsOnly.slice(-10) : digitsOnly || '';
      

      setSelectedCountryCode(normalizedCode);
      let firstName = user?.firstName || '';
      let lastName = user?.lastName || '';
      if (!firstName && user?.name) {
        const parts = String(user.name).trim().split(/\s+/);
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }
      const storedLanguage = localStorage.getItem('hm_language') || 'en';
      setInitialValues({
        name: firstName,
        lastName: lastName,
        email: user?.email || '',
        mobile: localPart,
        preferredLanguage: storedLanguage,
      });
    } catch { }
  };

  useEffect(() => {
    loadUserFromLocalStorage();
    const handler = () => loadUserFromLocalStorage();
    window.addEventListener('userDataChanged', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('userDataChanged', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const validationSchema = Yup.object().shape({
    name: nameValidation,
    lastName: optionalNameValidation,
    email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/,
        'Please enter a valid email address'
      )
      .required('Email is required'),
    mobile: Yup.string()
      .required('Please enter the mobile number')
      .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
      .test('len', 'Mobile number must be 9 digits or more', (val) => val?.length >= 9)
      .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
    preferredLanguage: Yup.string()
      .required('Please select a preferred language')
      .oneOf(['en', 'ar'], 'Please select a valid language'),
  });

  const formConfig = {
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values: any) => {
      const raw = localStorage.getItem('userData');
      const parsed = raw ? JSON.parse(raw) : {};
      const user = parsed?.userDetails || {};
      const id = user?.id || parsed?.id;

      const localPart = String(values.mobile || '').replace(/\D/g, '').slice(-10);
      let code = selectedCountryCode || '+962';
      if (code.startsWith('00')) code = `+${code.slice(2)}`;
      if (!code.startsWith('+')) code = `+${code}`;

      const payload = { firstName: values.name, lastName: values.lastName, email: values.email, mobileNo: `${code}${localPart}` };

      EditProfile(
        { id, data: payload },
        {
          onSuccess: () => {
            try {
              // Update language preference in localStorage
              localStorage.setItem('hm_language', values.preferredLanguage);
              
              const next = {
                ...(parsed || {}),
                userDetails: {
                  ...(parsed?.userDetails || {}),
                  name: values.name,
                  firstName: values.name,
                  lastName: values.lastName,
                  email: values.email,
                  mobileNo: `${code}${localPart}`,
                },
              };
              localStorage.setItem('userData', JSON.stringify(next));
              window.dispatchEvent(new Event('userDataChanged'));
              
              // Reload the page to apply language change
              window.location.reload();
            } catch { }
            onClose && onClose();
          },
        }
      );
    },
    fields: [
      { name: 'name', label: t('profile.firstName'), type: 'text', isRequired: true },
      { name: 'lastName', label: t('profile.lastName'), type: 'text', isRequired: true },
      { name: 'email', label: t('profile.email'), type: 'text', isRequired: true },
      {
        name: 'mobile',
        label: t('profile.mobileNo'),
        type: 'custom',
        isRequired: true,
        component: ({ field, form }: any) => (
          <PhoneNumberInput
            field={field}
            form={form}
            selectedCountryCode={selectedCountryCode}
            onCountryCodeChange={setSelectedCountryCode}
          />
        ),
      },
      {
        name: 'preferredLanguage',
        label: t('profile.preferredLanguage'),
        type: 'custom',
        isRequired: true,
        component: ({ field, form }: any) => (
          <FormControl component="fieldset" error={!!(form.touched.preferredLanguage && form.errors.preferredLanguage)}>
            {/* <FormLabel component="legend" sx={{ fontSize: '0.875rem', color: 'text.primary', mb: 1 }}>
              {t('profile.preferredLanguage')}
            </FormLabel> */}
            <RadioGroup
              {...field}
              value={field.value || 'en'}
              onChange={(e) => {
                field.onChange(e);
                form.setFieldValue('preferredLanguage', e.target.value);
              }}
              row
            >
              <FormControlLabel 
                value="en" 
                control={<Radio />} 
                label={t('profile.english')} 
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
              <FormControlLabel 
                value="ar" 
                control={<Radio />} 
                label={t('profile.arabic')} 
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            </RadioGroup>
            {form.touched.preferredLanguage && form.errors.preferredLanguage && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {form.errors.preferredLanguage}
              </Typography>
            )}
          </FormControl>
        ),
      },
    ],
    customButton: ({ handleSubmit, dirty, isValid }: any) => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
        <Button type="submit" variant="contained" disabled={!dirty || !isValid || EditProfileLoading} onClick={handleSubmit} sx={{ backgroundColor: COLORS.primary.main, textTransform: 'none', '&:hover': { backgroundColor: alpha(COLORS.primary.main, 0.8) } }}>
          {EditProfileLoading ? t('profile.updating') : t('profile.update')}
        </Button>
        {/* <Button variant="contained" sx={{ bgcolor: COLORS.warning?.main || '#f0ad4e' }} onClick={() => onClose && onClose()} type="button">
          Cancel
        </Button> */}
      </Box>
    ),
  };

  const FormComp = useMemo(() => allForm(formConfig), [initialValues, selectedCountryCode]);
  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>{t('profile.editProfile')}</Typography>
      <FormComp />
    </Box>
  );
};

const EditProfile: React.FC = () => {
  return <EditProfileForm />;
};

export default EditProfile;


